import { useCallback, useEffect, useRef } from 'react';
import { useLocation } from '@/providers/LocationProvider';
import {
  useSelfProfile,
  useUpsertSelfProfile,
  toSelfProfileUpdatePayload,
  Profile,
} from '@/services/profiles';
import { useUiConfig } from '@/services/config';
import { useAuth } from '@/providers/AuthProvider';
import { calculateDistance, convertDistanceToMeters } from '@/utils/distance';

/**
 * Hook that automatically syncs user's current location to their profile
 * based on configurable time intervals and distance thresholds
 */
export const useBackgroundLocationSync = () => {
  const { coords: currentCoords, status: locationStatus } = useLocation();
  const { data: config } = useUiConfig();
  const { user } = useAuth();
  const { data: profile } = useSelfProfile({
    enabled: Boolean(user),
  });
  const upsertSelfProfile = useUpsertSelfProfile();

  const lastUpdateRef = useRef<number>(0);
  const lastLocationRef = useRef<{ latitude: number; longitude: number } | null>(null);
  const updateTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const typedProfile = profile as Profile;
    // Don't sync if we don't have the necessary data
    if (
      !currentCoords ||
      !typedProfile ||
      !config?.location ||
      locationStatus !== 'granted'
    ) {
      return;
    }

    // Don't sync if update is already in progress
    if (upsertSelfProfile.isPending) {
      return;
    }

    const now = Date.now();
    const currentLocation = {
      latitude: currentCoords.latitude,
      longitude: currentCoords.longitude,
    };

    // Get config values with defaults
    const updateInterval = config.location.updateInterval ?? 600000; // 10 minutes default
    const distanceThreshold = config.location.distanceThreshold ?? 10; // 10m default
    const distanceUnit = config.location.unit ?? 'm';
    const significantDistance = config.user?.movedSignificantly?.distance ?? 100; // 100m default
    const significantUnit = config.user?.movedSignificantly?.unit ?? 'm';

    // Convert distance thresholds to meters for calculation
    const thresholdInMeters = convertDistanceToMeters(distanceThreshold, distanceUnit);
    const significantInMeters = convertDistanceToMeters(
      significantDistance,
      significantUnit,
    );

    const shouldUpdateByTime = () => {
      return now - lastUpdateRef.current >= updateInterval;
    };

    const shouldUpdateByDistance = () => {
      if (!lastLocationRef.current) {
        return true; // First time, always update
      }

      const distance = calculateDistance(
        lastLocationRef.current.latitude,
        lastLocationRef.current.longitude,
        currentLocation.latitude,
        currentLocation.longitude,
      );

      // Check if moved beyond either threshold
      return distance >= thresholdInMeters || distance >= significantInMeters;
    };

    const shouldUpdateByAccuracy = () => {
      if (!typedProfile.location?.accuracy || !currentCoords.accuracy) {
        return false;
      }

      // Update if we got significantly better accuracy
      return currentCoords.accuracy < typedProfile.location.accuracy * 0.5;
    };

    // Determine if we should update
    const shouldUpdate =
      shouldUpdateByTime() || shouldUpdateByDistance() || shouldUpdateByAccuracy();

    if (!shouldUpdate) {
      return;
    }

    // Clear any pending timeout
    if (updateTimeoutRef.current) {
      clearTimeout(updateTimeoutRef.current);
      updateTimeoutRef.current = null;
    }

    // Debounce updates to avoid rapid-fire updates
    updateTimeoutRef.current = setTimeout(() => {
      const updatedProfile = {
        ...typedProfile,
        location: {
          latitude: currentCoords.latitude,
          longitude: currentCoords.longitude,
          accuracy: currentCoords.accuracy,
          timestamp: Date.now(),
        },
      };

      console.info('[BackgroundLocationSync] Updating profile location', {
        previousLocation: typedProfile.location,
        newLocation: updatedProfile.location,
        reason: {
          byTime: shouldUpdateByTime(),
          byDistance: shouldUpdateByDistance(),
          byAccuracy: shouldUpdateByAccuracy(),
        },
        config: {
          updateInterval,
          distanceThreshold: thresholdInMeters,
          significantDistance: significantInMeters,
        },
      });

      upsertSelfProfile.mutate(toSelfProfileUpdatePayload(updatedProfile), {
        onSuccess: () => {
          lastUpdateRef.current = now;
          lastLocationRef.current = currentLocation;
          console.debug('[BackgroundLocationSync] Profile location updated successfully');
        },
        onError: (error) => {
          console.error(
            '[BackgroundLocationSync] Failed to update profile location',
            error,
          );
        },
      });
    }, 2000); // 2 second debounce

    return () => {
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
        updateTimeoutRef.current = null;
      }
    };
  }, [
    currentCoords,
    profile,
    config?.location,
    config?.user?.movedSignificantly,
    locationStatus,
    upsertSelfProfile,
  ]);

  // Initialize last location ref when profile is first loaded
  useEffect(() => {
    const typedProfile = profile as Profile;
    if (typedProfile?.location && !lastLocationRef.current) {
      lastLocationRef.current = {
        latitude: typedProfile.location.latitude,
        longitude: typedProfile.location.longitude,
      };

      // Set last update time based on profile's location timestamp
      if (typedProfile.location.timestamp) {
        lastUpdateRef.current = typedProfile.location.timestamp;
      }
    }
  }, [(profile as Profile)?.location]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
    };
  }, []);

  const forceLocationUpdate = useCallback(async () => {
    const typedProfile = profile as Profile;
    if (!currentCoords || !typedProfile || upsertSelfProfile.isPending) {
      return Promise.reject(
        new Error('Cannot update location: missing data or update in progress'),
      );
    }

    return new Promise<void>((resolve, reject) => {
      const updatedProfile = {
        ...typedProfile,
        location: {
          latitude: currentCoords.latitude,
          longitude: currentCoords.longitude,
          accuracy: currentCoords.accuracy,
          timestamp: Date.now(),
        },
      };

      console.info('[BackgroundLocationSync] Force updating profile location');

      upsertSelfProfile.mutate(toSelfProfileUpdatePayload(updatedProfile), {
        onSuccess: () => {
          lastUpdateRef.current = Date.now();
          lastLocationRef.current = {
            latitude: currentCoords.latitude,
            longitude: currentCoords.longitude,
          };
          console.debug('[BackgroundLocationSync] Force update successful');
          resolve();
        },
        onError: (error) => {
          console.error('[BackgroundLocationSync] Force update failed', error);
          reject(error);
        },
      });
    });
  }, [currentCoords, profile, upsertSelfProfile]);

  return {
    isUpdating: upsertSelfProfile.isPending,
    lastError: upsertSelfProfile.error,
    lastUpdate: lastUpdateRef.current,
    forceLocationUpdate,
  };
};
